import styled from 'styled-components';

import { BasicParagraph } from '../../../../foundations/typefaces/BasicParagraph';
import style from '../../../../style';
import type { Size as ThemeSize } from '../../../../style/types/theme';
import { Size } from './types';

const getHeight = (size: ThemeSize, $size: Size, $withoutEllipsis: boolean = true) => {
  if ($withoutEllipsis) {
    return 'auto';
  }

  switch ($size) {
    case Size.MEDIUM:
      return size.units(5);
    case Size.SMALL:
    default:
      return size.units(4);
  }
};

export const LeftContainer = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
}));

export const LeftExternalComponent = styled.i(({ theme: { color, transition } }) => ({
  color: color.content.secondary.default(),
  transition: transition.standard('color'),
}));

export const RightExternalComponent = styled.i(({ theme: { color, transition } }) => ({
  color: color.content.secondary.default(),
  transition: transition.standard('color'),
}));

export const CenterContainer = styled.div(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
}));

export const RightComponentWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  paddingLeft: size.units(1),
  flexShrink: 0,
}));

interface StyledBasicParagraphProps {
  $isDarkMode?: boolean;
}

export const StyledBasicParagraph = styled(BasicParagraph)<StyledBasicParagraphProps>(
  ({ theme: { color, transition }, $isDarkMode }) => ({
    color: $isDarkMode
      ? color.content.reversed.default()
      : color.content.secondary.default(),
    transition: transition.standard('color'),
  }),
);

export const EllipsisBasicParagraph = styled(StyledBasicParagraph)({
  ...style.mixins.ellipsis(true),
});

interface WrapperProps {
  $isClickable: boolean;
  $isDisabled: boolean;
  $isHovered: boolean;
  $rightComponentHoverColor?: string;
  $size: Size;
  $withoutEllipsis?: boolean;
  $isDarkMode?: boolean;
}

export const Wrapper = styled.div<WrapperProps>(
  ({
    $isClickable,
    $isDisabled,
    $isHovered,
    $rightComponentHoverColor,
    $size,
    $withoutEllipsis = false,
    $isDarkMode,
    theme: { size, color, transition },
  }) => ({
    width: '100%',
    backgroundColor: $isDarkMode
      ? color.background.reversed.default()
      : color.background.primary.default(),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: getHeight(size, $size, $withoutEllipsis),
    padding: $withoutEllipsis ? size.units(1) : `0px ${size.units(1)}`,
    borderRadius: size.units(0.5),
    transition: transition.standard('background-color'),
    cursor: 'default',

    ':focus-visible': {
      border: `1px solid ${color.border.accent.default()}`,
    },

    ...style.mixins.devices.desktop({
      ...($isClickable && {
        '&:hover': {
          backgroundColor: $isDarkMode
            ? color.background.tertiary.default(0.05)
            : color.background.primary.hover(),
          cursor: 'pointer',

          [StyledBasicParagraph]: {
            color: $isDarkMode
              ? color.content.reversed.default()
              : color.content.secondary.hover(),
          },

          [LeftExternalComponent]: {
            color: color.content.secondary.hover(),
          },

          [RightExternalComponent]: {
            color: $rightComponentHoverColor || color.content.secondary.hover(),
          },
        },
      }),
    }),

    ...($isHovered && {
      backgroundColor: $isDarkMode
        ? color.background.tertiary.default(0.05)
        : color.background.primary.hover(),
      cursor: 'pointer',

      [StyledBasicParagraph]: {
        color: $isDarkMode
          ? color.content.reversed.default()
          : color.content.secondary.hover(),
      },

      [LeftExternalComponent]: {
        color: color.content.secondary.hover(),
      },

      [RightExternalComponent]: {
        color: $rightComponentHoverColor || color.content.secondary.hover(),
      },
    }),

    [LeftContainer]: {
      marginRight: size.units(1.5),
    },

    ...($isDisabled && {
      cursor: 'not-allowed',

      [StyledBasicParagraph]: {
        color: color.content.secondary.disabled(),
      },

      [LeftExternalComponent]: {
        color: color.content.secondary.disabled(),
      },

      [RightExternalComponent]: {
        color: color.content.secondary.disabled(),
      },
    }),
  }),
);

export const PremiumWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  verticalAlign: 'baseline',
  flexShrink: 0,
  paddingLeft: size.units(1),
}));

export const TextLabelWrapper = styled.div(({ theme: { size } }) => ({
  paddingLeft: size.units(2),
}));
